import React, { useEffect, useState } from 'react';
import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer';
import LatoRegular from "../../utils/fonts/Lato-Regular.ttf";
import LatoBold from '../../utils/fonts/Lato-Bold.ttf';
import { formatNumber } from '../../utils/Utils';

const OrderPDF = ({ page_data, tenant_info }) => {
 const [pdf_data, setPDFData] = useState(page_data);

 Font.register({
  family: 'Lato',
  fonts: [
   { src: LatoRegular },
   { src: LatoBold, fontWeight: 'bold' }
  ]
 });

 useEffect(() => {
  setPDFData(page_data);
 }, [page_data]);

 const styles = {
  page: {
   padding: 30,
   paddingBottom: 60, // Increased padding to prevent footer overlap
   fontFamily: "Lato",
   color: "#333",
   fontSize: 9, // Slightly smaller font
   lineHeight: 1.2 // Tighter line height
  },
  header: {
   marginBottom: 15,
   display: 'flex',
   flexDirection: 'row',
   alignItems: 'flex-start',
   justifyContent: 'space-between'
  },
  logoContainer: {
   flex: 1
  },
  logo: {
   width: 80, // Smaller logo
   height: 'auto',
   maxHeight: 50
  },
  titleContainer: {
   flex: 2,
   textAlign: 'center',
   paddingTop: 5
  },
  title: {
   fontSize: 16, // Smaller title
   fontWeight: 'bold',
   color: '#2c3e50',
   marginBottom: 2
  },
  subtitle: {
   fontSize: 12, // Smaller subtitle
   color: '#7f8c8d',
   marginTop: 10
  },
  infoContainer: {
   flex: 1,
   textAlign: 'right'
  },
  infoText: {
   fontSize: 8, // Smaller info text
   color: '#7f8c8d',
   marginBottom: 1
  },
  section: {
   marginBottom: 12
  },
  card: {
   backgroundColor: '#f8f9fa',
   borderRadius: 3,
   padding: 10,
   marginBottom: 12,
   borderLeft: '2px solid #3498db'
  },
  grid: {
   display: 'flex',
   flexDirection: 'row',
   flexWrap: 'wrap',
   justifyContent: 'space-between'
  },
  gridItem: {
   width: '48%', // Wider items for better spacing
   marginBottom: 6
  },
  label: {
   fontWeight: 'bold',
   color: '#34495e',
   marginBottom: 1,
   fontSize: 8
  },
  value: {
   color: '#2c3e50',
   fontSize: 8
  },
  table: {
   width: '100%',
   borderCollapse: 'collapse',
   marginBottom: 12
  },
  tableHeader: {
   backgroundColor: '#3498db',
   color: 'white',
   fontWeight: 'bold',
   padding: 5,
   textAlign: 'center',
   fontSize: 8
  },
  tableRow: {
   borderBottom: '1px solid #ddd',
   flexDirection: 'row',
   minHeight: 20
  },
  tableCell: {
   padding: 4,
   textAlign: 'center',
   fontSize: 8
  },
  footer: {
   position: 'absolute',
   bottom: 20,
   left: 30,
   right: 30,
   textAlign: 'center',
   fontSize: 8,
   color: '#95a5a6',
   paddingTop: 8,
   borderTop: '1px solid #eee'
  },
  totalRow: {
   backgroundColor: '#f8f9fa',
   fontWeight: 'bold',
   padding: 6,
   borderTop: '2px solid #3498db'
  },
  highlight: {
   backgroundColor: '#e3f2fd'
  },
  pageNumber: {
   position: 'absolute',
   bottom: 22,
   right: 30,
   fontSize: 8,
   color: '#95a5a6'
  },
  // New style for text wrapping
  wrapText: {
   flexWrap: 'wrap',
   textOverflow: 'ellipsis'
  }
 };

 let orderItems = pdf_data?.order_items?.filter((item) => item?.status !== "cancelled") || [];

 // Calculate totals
 const totalUnits = orderItems.reduce((acc, item) => acc + (item?.units || 0), 0);
 const totalValue = orderItems.reduce((acc, item) => acc + (item?.total_value || 0), 0);

 // Define column widths for the table - adjusted for better fit
 const columnWidths = {
  product: '20%',
  brand: '12%',
  category: '12%',
  color: '10%',
  size: '8%',
  units: '8%',
  unitValue: '12%',
  totalValue: '18%'
 };

 return (
  <Document title={pdf_data?.order_ref_number ? `${pdf_data?.order_name} ${pdf_data?.order_ref_number} Order` : "Order"}>
   <Page size="A4" orientation="landscape" style={styles.page} wrap>
    {/* Header - Fixed on each page */}
    <View fixed>
     <View style={styles.header}>
      <View style={styles.logoContainer}>
       {tenant_info?.tenant_logo && (
        <Image style={styles.logo} src={tenant_info?.tenant_logo} />
       )}
      </View>

      <View style={styles.titleContainer}>
       <Text style={styles.title}>{tenant_info?.name}</Text>
       <Text style={styles.subtitle}>Order Statement</Text>
      </View>

      <View style={styles.infoContainer}>
       <Text style={styles.infoText}>Date: {new Date().toLocaleDateString()}</Text>
       <Text style={styles.infoText}>Ref: {pdf_data?.order_ref_number || "--"}</Text>
      </View>
     </View>

     {/* Order Details */}
     <View style={styles.card}>
      <View style={styles.grid}>
       {[
        { label: "Reference No:", value: pdf_data?.order_ref_number },
        { label: "Sales Representative:", value: pdf_data?.sales_rep?.name },
        { label: "Destination:", value: pdf_data?.order_name },
        { label: "Mobile Number:", value: pdf_data?.sales_rep?.mobile_number },
        { label: "Email Address:", value: pdf_data?.sales_rep?.email_address },
        { label: "Warehouse:", value: pdf_data?.warehouse_site_name }
       ].map((item, index) => (
        <View key={index} style={styles.gridItem}>
         <Text style={styles.label}>{item.label}</Text>
         <Text style={styles.value}>{item.value || "--"}</Text>
        </View>
       ))}
      </View>
     </View>
    </View>

    {/* Order Items Table */}
    {orderItems.length > 0 && (
     <View style={styles.section}>
      {/* Table Header */}
      <View style={{ ...styles.tableRow, ...styles.tableHeader }}>
       <Text style={{ ...styles.tableCell, width: columnWidths.product }}>Product</Text>
       <Text style={{ ...styles.tableCell, width: columnWidths.brand }}>Brand</Text>
       <Text style={{ ...styles.tableCell, width: columnWidths.category }}>Category</Text>
       <Text style={{ ...styles.tableCell, width: columnWidths.color }}>Color</Text>
       <Text style={{ ...styles.tableCell, width: columnWidths.size }}>Size</Text>
       <Text style={{ ...styles.tableCell, width: columnWidths.units }}>Units</Text>
       <Text style={{ ...styles.tableCell, width: columnWidths.unitValue }}>Unit Value (ZMW)</Text>
       <Text style={{ ...styles.tableCell, width: columnWidths.totalValue }}>Total Value (ZMW)</Text>
      </View>

      {/* Table Rows */}
      {orderItems.map((item, index) => (
       <View key={index} style={{ ...styles.tableRow, ...(index % 2 === 0 ? styles.highlight : {}) }}>
        <Text style={{ ...styles.tableCell, width: columnWidths.product, ...styles.wrapText }}>
         {item.product?.name || "--"}
        </Text>
        <Text style={{ ...styles.tableCell, width: columnWidths.brand, ...styles.wrapText }}>
         {item.product?.category || "--"}
        </Text>
        <Text style={{ ...styles.tableCell, width: columnWidths.category, ...styles.wrapText }}>
         {item.product?.product_type || "--"}
        </Text>
        <Text style={{ ...styles.tableCell, width: columnWidths.color, ...styles.wrapText }}>
         {item.color || "--"}
        </Text>
        <Text style={{ ...styles.tableCell, width: columnWidths.size, ...styles.wrapText }}>
         {item.size || "--"}
        </Text>
        <Text style={{ ...styles.tableCell, width: columnWidths.units }}>
         {item.units || "--"}
        </Text>
        <Text style={{ ...styles.tableCell, width: columnWidths.unitValue }}>
         {formatNumber(item.unit_value || 0.0) || "--"}
        </Text>
        <Text style={{ ...styles.tableCell, width: columnWidths.totalValue }}>
         {formatNumber(item.total_value || 0.0) || "--"}
        </Text>
       </View>
      ))}

      {/* Total Row */}
      <View style={{ ...styles.tableRow, ...styles.totalRow }}>
       <Text style={{ ...styles.tableCell, textAlign: 'right', width: '62%' }}>TOTALS:</Text>
       <Text style={{ ...styles.tableCell, width: columnWidths.units }}>{totalUnits}</Text>
       <Text style={{ ...styles.tableCell, width: columnWidths.unitValue }}></Text>
       <Text style={{ ...styles.tableCell, width: columnWidths.totalValue }}>{formatNumber(totalValue) || 0.0}</Text>
      </View>
     </View>
    )}

    {/* Footer */}
    <View style={styles.footer} fixed>
     <Text>Generated on {new Date().toLocaleDateString()} | {tenant_info?.name}</Text>
     <Text>Ref: {pdf_data?.order_ref_number || "--"}</Text>
    </View>

    {/* Page Number */}
    <Text
     style={styles.pageNumber}
     render={({ pageNumber, totalPages }) => (
      `Page ${pageNumber} of ${totalPages}`
     )}
     fixed
    />
   </Page>
  </Document>
 );
};

export default OrderPDF;